import { 
  Component, 
  Input, 
  Output, 
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface IconConfig {
  name: string;
  size?: 'small' | 'medium' | 'large';
  color?: string;
  spin?: boolean;
  clickable?: boolean;
}

/**
 * Componente Icon simplificado - Solo UI, sin arquitectura hexagonal
 */
@Component({
  selector: 'sura-icon',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (config.name) {
      <div 
        [attr.data-testid]="'icon-' + config.name"
        [class.cursor-pointer]="config.clickable">
        @if (isCustomIcon()) {
          <svg 
            [attr.width]="svgSize" 
            [attr.height]="svgSize" 
            [style.cursor]="config.clickable ? 'pointer' : 'default'"
            [innerHTML]="getCustomIconSvg()"
            (click)="handleClick($event)">
          </svg>
        } @else {
          <i 
            [class]="iconClasses"
            [style.color]="config.color"
            [style.cursor]="config.clickable ? 'pointer' : 'default'"
            [style.font-size]="fontSize"
            (click)="handleClick($event)"
          ></i>
        }
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SuraIconComponent {
  
  private _config!: IconConfig;
  private cdr = inject(ChangeDetectorRef);
  
  @Input({ required: true }) 
  set config(value: IconConfig) {
    this._config = value;
    this.cdr.markForCheck();
  }
  
  get config(): IconConfig {
    return this._config || { name: '' };
  }
  @Output() iconClick = new EventEmitter<Event>();

  get iconClasses(): string {
    if (!this.config?.name) return '';
    
    const classes = [`pi pi-${this.config.name}`];
    
    if (this.config.spin) {
      classes.push('pi-spin');
    }
    
    return classes.join(' ');
  }

  get fontSize(): string {
    if (this.config?.size) {
      const sizeMap = {
        'small': '14px',
        'medium': '16px', 
        'large': '24px'
      };
      return sizeMap[this.config.size];
    }
    return '16px';
  }

  get svgSize(): string {
    if (this.config?.size) {
      const sizeMap = {
        'small': '14',
        'medium': '16', 
        'large': '24'
      };
      return sizeMap[this.config.size];
    }
    return '16';
  }

  isCustomIcon(): boolean {
    return this.config?.name ? ['menu', 'hamburger', 'bars'].includes(this.config.name) : false;
  }

  getCustomIconSvg(): string {
    if (!this.config?.name) return '';
    
    const color = this.config.color || 'currentColor';
    
    switch(this.config.name) {
      case 'menu':
      case 'hamburger':
      case 'bars':
        return `<path fill="${color}" d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/>`;
      default:
        return '';
    }
  }

  handleClick(event: Event): void {
    if (this.config?.clickable) {
      this.iconClick.emit(event);
    }
  }
}